import ValidateModule from "./ValidateModule";

import type { SortOrder } from "@esWebAPI/es/model/Feature";
import type {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import type {Criteria} from "@esWebAPI/es/model/Criteria";
import type {Address, IRecommendation, Schedule, Service, Settings, Shift, SavedLocation, ScheduleMeta} from "@esWebAPI/es/model/Schedule";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { IEventChannel, ApiOpts } from "../model/Models";
import type CacheModule from "./CacheModule";

type Objective ='min:completion_time'
    | 'min-max:completion_time'
    | 'min:activities'
    | 'min-max:activities'
    | 'min:transport_time'
    | 'min-max:transport_time'

export default class ScheduleModule {

    private readonly rqBroker: AjaxRequestBroker;
    private eventChannel: IEventChannel;
    private readonly resourceName: string;
    private readonly cache: CacheModule;

    constructor (eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;
        this.cache = cache;


        this.eventChannel.addListener("onDestroy", () => {
            this.eventChannel = null; // prevent memory leaks when the map widget is destroyed
        });
    }

        generate(id: number, objective?: Objective,  opts?: ApiOpts): JQueryPromise<Schedule> {
            opts = $.extend(opts, {'resolveWithXHR': true});
            
            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName + "/" + id + "/generate" + (objective ? "?objective=" + objective : "")),
                opts: opts,
                headers : {'es-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone}
            }).then((rs: Schedule, jqXHR: JQuery.jqXHR) => {
                this.cache.clear(this.resourceName);
                
                const geoMapId = parseInt(jqXHR.getResponseHeader("es_geomap_id") as string);
                this.cache.clear("GeoMap", geoMapId);
                this.cache.clear("LayerView_GeoMap", geoMapId);
                this.cache.clear("LayerMeta_GeoMap", geoMapId);
                this.cache.clear("LayerView");
                this.cache.clear("LayerInfo");
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerView_LayerInfo");
                
                if (this.eventChannel){
                    this.eventChannel.fireEvent("onGenerateSchedule", rs);
                }
                return rs;
            });
        }

        // This is mainly for mobile and allows to regenerate just a shift (a day)
        regenerate(id: number, shiftId: string, objective?: Objective, opts?: ApiOpts): JQueryPromise<Schedule> {
            opts = $.extend(opts, {'resolveWithXHR': true});

            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName + "/" + id + "/" + shiftId + "/regenerate" + (objective ? "?objective=" + objective : "")),
                opts: opts,
                headers : {'es-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone}
            }).then((rs: Schedule, jqXHR: JQuery.jqXHR) => {
                this.cache.clear(this.resourceName);

                const geoMapId = parseInt(jqXHR.getResponseHeader("es_geomap_id") as string);
                this.cache.clear("GeoMap", geoMapId);
                this.cache.clear("LayerView_GeoMap", geoMapId);
                this.cache.clear("LayerMeta_GeoMap", geoMapId);
                this.cache.clear("LayerView");
                this.cache.clear("LayerInfo");
                this.cache.clear("LayerInfo_LayerView");
                this.cache.clear("LayerView_LayerInfo");

                if (this.eventChannel){
                    this.eventChannel.fireEvent("onGenerateSchedule", rs);
                }
                return rs;
            });
        }

        getByGeoMapId(geoMapId: number, opts?: ApiOpts): JQueryPromise<Schedule> {
            if (!ValidateModule.checkId(geoMapId)) {
                return es.util.deferred.rejected();
            }

            return this.rqBroker.doGet({
                url: es.api.getURL(this.resourceName + "/get_by_geomap/" + geoMapId),
                opts: opts
            });
        }

        /**
         * Save schedule settings to the server
         */
        updateSettings(id:number, settings: Settings, opts?: ApiOpts): JQuery.Deferred<Settings>{
            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName + "/" + id + "/settings"),
                data: settings,
                opts: opts
            }).done((rs: Settings) => {
                this.cache.clear(this.resourceName);
                if (this.eventChannel){
                    this.eventChannel.fireEvent("onUpdateScheduleSettings", rs);
                }
            });
        }

        /**
         * Overwrites all services (PUT action)
         */
        updateServices(id: number, services:Service[] = [], opts?: ApiOpts): JQuery.Deferred<Service[]>{
            if(!services || services.length === 0){
                console.warn("Setting services as an empty array");
            }
            for (let i = 0; i < services.length; i++) {
                services[i].$type = "EsService";
            }
            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName + "/" + id + "/services"),
                data: services,
                opts: opts
            }).done((rs: Service[]) => {
                this.cache.clear(this.resourceName);
                if (this.eventChannel){
                    this.eventChannel.fireEvent("onUpdateScheduleServices", rs);
                }
            });
        }

        /**
         * Overwrites all shifts (PUT action)
         */
        updateShifts(id: number, shifts:Shift[] = [], opts?: ApiOpts): JQuery.Deferred<Shift[]>{
            if(!shifts || shifts.length === 0){
                console.warn("Setting shifts as an empty array");
            }
            for (let i = 0; i < shifts.length; i++) {
                shifts[i].$type = "EsShift";
            }
            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName + "/" + id + "/shifts"),
                data: shifts,
                opts: opts
            }).done((rs: Shift[]) => {
                this.cache.clear(this.resourceName);
                if (this.eventChannel){
                    this.eventChannel.fireEvent("onUpdateScheduleShifts", rs);
                }
            });
        }

        getServicesAvailability(scheduleId: number): JQueryPromise<any> {
            if(!ValidateModule.checkId(scheduleId)) {
                return es.util.deferred.rejected();
            }
            return this.rqBroker.doGet({
                url: es.api.getURL(this.resourceName + "/" + scheduleId + "/availability"),
                opts: {noCache: true}
            });
        }

        checkFeasibility(id: number, startAddress: Address, finishAddress: Address, startTime: number, finishTime: number, services: Service[], opts?: ApiOpts): JQueryPromise<Service[]> {
            if(
            !ValidateModule.checkId(id) ||
            !ValidateModule.checkMandatoryArg(startAddress) ||
            !ValidateModule.checkMandatoryArg(finishAddress) ||
            !ValidateModule.checkMandatoryArg(startTime) ||
            !ValidateModule.checkMandatoryArg(finishTime) ||
            !ValidateModule.checkMandatoryArray(services)) {
             return es.util.deferred.rejected();
            }

            for (let i = 0; i < services.length; i++) {
                services[i].$type = "EsService";
            }

            const resourceObj = {
                startAddress,
                finishAddress,
                startTime,
                finishTime,
                services
            };

            return this.rqBroker.doPost({
                url: es.api.getURL(`${this.resourceName}/${id}/check_feasibility`),
                opts: opts,
                data: resourceObj,
                headers : {'es-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone}
            }).then(function(rs: Service[]){
                return es.util.deferred.resolved(rs);
            });

        }

        listOwned(criteria?:Criteria, sortOrder?:SortOrder, offset?:number, length?:number, usedAfter?: number, opts?: ApiOpts): JQuery.Deferred<ScheduleMeta[]>{
            return this.cache.doGetList(this.rqBroker, this.resourceName + "/list_owned/", criteria, sortOrder, offset, length, usedAfter, opts);
        }

        listShared(criteria?:Criteria, sortOrder?:SortOrder, offset?:number, length?:number, usedAfter?: number, opts?: ApiOpts): JQuery.Deferred<ScheduleMeta[]> {
            return this.cache.doGetList(this.rqBroker, this.resourceName + "/list_shared/", criteria, sortOrder, offset, length, usedAfter, opts);
        }

        listByDateRange(start: number, end: number, opts?: ApiOpts) {
            if (!ValidateModule.checkMandatoryArg(start) || !ValidateModule.checkMandatoryArg(end)) {
                return es.util.deferred.rejected("missing mandatory argument start or end");
            }

            return this.rqBroker.doGet({
                url: es.api.getURL('schedule_mobile') + '/list_by_date_range?start=' + start + "&end=" + end, // initially done for mobile but could be useful in the regular api
                opts: opts
            });
        }

        // ---------- SavedLocation ----------
        createSavedLocation(resourceObj: SavedLocation, opts?: ApiOpts): JQueryPromise<SavedLocation>{
            if (!ValidateModule.checkMandatoryArg(resourceObj)) {
                return es.util.deferred.rejected();
            }

            return this.rqBroker.doPost({
                url: es.api.getURL(this.resourceName + "/saved_location"),
                opts: opts,
                data: resourceObj
            }).then((rs: SavedLocation) => {
                this.cache.setEntity(this.resourceName + "_saved_location", rs);
                this.cache.clear(this.resourceName + "_saved_location", 'list');
                if (this.eventChannel){
                    this.eventChannel.fireEvent("onCreateSavedLocation", rs);
                }
                return es.util.deferred.resolved(rs);
            });
        }

        removeSavedLocation(id: number, opts?: ApiOpts): JQueryPromise<number>{
            if (!ValidateModule.checkId(id)) {
                return es.util.deferred.rejected();
            }

            return this.rqBroker.doDelete({
                url: es.api.getURL(this.resourceName + "/saved_location/" + id),
                opts: opts
            }).then((rs: SavedLocation, _jqXHR: JQuery.jqXHR) => {
                this.cache.clear(this.resourceName + "_saved_location", id);
                this.cache.clear(this.resourceName + "_saved_location", 'list');
                if (this.eventChannel){
                    this.eventChannel.fireEvent("onRemoveSavedLocation", rs);
                }
                return es.util.deferred.resolved(id);
            });
        }

        updateSavedLocation(resourceObj: SavedLocation, opts?: ApiOpts): JQueryPromise<SavedLocation>{
            if (!ValidateModule.checkMandatoryArg(resourceObj)) {
                return es.util.deferred.rejected();
            }
            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName + "/saved_location/" + resourceObj.id),
                data: resourceObj,
                opts: opts
            }).then((rs: SavedLocation) => {
                this.cache.setEntity(this.resourceName + "_saved_location", rs);
                this.cache.clear(this.resourceName + "_saved_location", 'list');
                if (this.eventChannel){
                    this.eventChannel.fireEvent("onUpdateSavedLocation", rs);
                }
                return es.util.deferred.resolved(rs);
            });
        }

        listSavedLocations(criteria?: Criteria, sortOrder?:SortOrder, offset?:number, length?:number, opts?: ApiOpts): JQuery.Deferred<SavedLocation[]>{
            return this.cache.doGetList(this.rqBroker, this.resourceName + "/saved_location/", criteria, sortOrder, offset, length, opts);
        }

        // ---------- Recommendations ----------
        
        getRecommendation(layerViewId: number, id: number, opts?: ApiOpts): JQueryPromise<IRecommendation> {
            if (!ValidateModule.checkId(id) || !ValidateModule.checkId(layerViewId)) {
                return es.util.deferred.rejected();
            }
            
            const url = es.api.getURL(this.resourceName) + "/recommendation/" + id + "?layer_view_id=" + layerViewId;
            return this.cache.doGet(this.rqBroker, this.resourceName + "_recommendation", url, id, opts);
        }
        
        getRecommendationsForGeoMap(geoMapId: number, opts?: ApiOpts): JQueryPromise<IRecommendation[]> {
            if (!ValidateModule.checkId(geoMapId)) {
                return es.util.deferred.rejected();
            }

            return this.rqBroker.doGet({
                url: es.api.getURL(this.resourceName + "/recommendation/geomap/" + geoMapId),
                opts: opts
            }).done((recommendations: IRecommendation[]) => {
                recommendations.forEach(r => this.cache.setEntity(this.resourceName + "_recommendation", r));
            });
        }
        
        createRecommendation(layerViewId: number, recommendation: IRecommendation, opts?: ApiOpts): JQueryPromise<IRecommendation> {
            if (!ValidateModule.checkId(layerViewId)) {
                return es.util.deferred.rejected();
            }
            
            if (!ValidateModule.checkMandatoryArg(recommendation)) {
                return es.util.deferred.rejected();
            }
            
            return this.rqBroker.doPost({
                url: es.api.getURL(this.resourceName + "/recommendation?layer_view_id=" + layerViewId),
                data: recommendation,
                opts: opts
            }).done((recommendation: IRecommendation) => {
                if (this.eventChannel) {
                    this.eventChannel.fireEvent("onCreateRecommendation", layerViewId, recommendation);
                }
            });
        }
        
        private refreshInfoAndfireOnUpdateLayerView(layerView: ILayerView): Promise<[ILayerView,  LayerInfo]> {
            this.cache.clear("GeoMap", layerView.geoMapId);
            this.cache.clear("LayerInfo", layerView.id);
            this.cache.clear("LayerInfo_LayerView", layerView.id);
            this.cache.clear("LayerView_LayerInfo", layerView.id);
            
            return Promise.all([
                es.api.layerInfo.getForLayerView(layerView.id),
                es.api.geoMap.get(layerView.geoMapId),
                this.getByGeoMapId(layerView.geoMapId)
            ]).then(([layerInfo, geoMap, _schedule]) => {
                if (this.eventChannel) {
                    this.eventChannel.fireEvent("onUpdateLayerView", layerView, layerInfo, geoMap.lastModified);
                }
                
                return Promise.resolve([layerView, layerInfo]);
            });
        }
        
        updateRecommendation(layerViewId: number, recommendation: IRecommendation, opts?: ApiOpts): JQueryPromise<IRecommendation> {
            if (!ValidateModule.checkMandatoryArg(recommendation) || !ValidateModule.checkId(recommendation.id)) {
                return es.util.deferred.rejected();
            }
            
            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName + "/recommendation/" + recommendation.id + "?layer_view_id=" + layerViewId),
                data: recommendation,
                opts: opts
            }).done((recommendation: IRecommendation) => {
                this.cache.setEntity(this.resourceName + "_recommendation", recommendation);
                
                this.cache.clear("LayerInfo", layerViewId);
                this.cache.clear("LayerInfo_LayerView", layerViewId);
                this.cache.clear("LayerView_LayerInfo", layerViewId);
                
                if (this.eventChannel) {
                    this.eventChannel.fireEvent("onUpdateRecommendation", layerViewId, recommendation);
                }
            });
        }
        
        removeRecommendation(layerViewId: number, id: number, opts?: ApiOpts): JQueryPromise<ILayerView> {
            if (!ValidateModule.checkId(id) || !ValidateModule.checkId(layerViewId)) {
                return es.util.deferred.rejected();
            }
            
            return es.api.layerView.getFeatureSourceCriterias(layerViewId).then((oldFSCriterias) => {
                return this.rqBroker.doDelete({
                    url: es.api.getURL(this.resourceName + "/recommendation/" + id + "?layer_view_id=" + layerViewId),
                    opts: opts
                }).then(() => oldFSCriterias);
            }).then((oldFSCriterias) => {
                this.cache.clear(this.resourceName + "_recommendation", id);
                this.cache.clear("LayerView", layerViewId);
                
                return Promise.all([
                    es.api.layerView.get(layerViewId),
                    es.api.layerView.getFeatureSourceCriterias(layerViewId, {noCache: true}),
                    es.util.deferred.resolved(oldFSCriterias)
                ]);
            }).then(([layerView, fsCriterias, oldFSCriterias]) => {
                return this.refreshInfoAndfireOnUpdateLayerView(layerView).then(() => {
                    if (this.eventChannel) {
                        if (fsCriterias?.lastModified != oldFSCriterias?.lastModified) {
                            this.eventChannel.fireEvent("onUpdateLayerViewRemovableCriteria", fsCriterias, layerViewId);
                        }
                        this.eventChannel.fireEvent("onRemoveRecommendation", layerViewId);
                        // TODO fire other events?
                    }
                    
                    return layerView;
                });
            });
        }
        
        setActiveRecommendation(layerViewId: number, id: number, opts?: ApiOpts): JQueryPromise<ILayerView> {
            if (!ValidateModule.checkId(layerViewId) || !ValidateModule.checkId(id)) {
                return es.util.deferred.rejected();
            }
            
            opts = $.extend(opts, {'resolveWithXHR': true});
            
            return this.rqBroker.doPut({
                url: es.api.getURL(this.resourceName + "/recommendation/active/" + id + "/" + layerViewId),
                opts: opts
            }).then((layerView : ILayerView) => {
                this.cache.setEntity("LayerView", layerView);
                
                return this.refreshInfoAndfireOnUpdateLayerView(layerView);
            }).then(([layerView, layerInfo]: [ILayerView, LayerInfo]) => {
                if (this.eventChannel) {
                    this.eventChannel.fireEvent("onSetActiveRecommendation", layerView, layerInfo)
                    // TODO fire other events?
                }
                
                return layerView;
            });
        }

        getStopCandidates(id: number, layerViewId: number, shiftId: string, location: [number, number], sortOrder: SortOrder, distanceKms: number, maxCandidates: number, opts?: ApiOpts) {
            if (!ValidateModule.checkId(id)) {
                return es.util.deferred.rejected();
            }

            let url = es.api.getURL("schedule_mobile/" + id + "/" + layerViewId + "/candidates");

            const params = {
                "sort_order": sortOrder,
                "shiftId": shiftId,
                "location": location,
                "distance_kms": distanceKms ? distanceKms : 100,
                "max_candidates": maxCandidates ? maxCandidates : 0
            };
            url = es.util.url.fromParams(params, url + "?", true);
            
            return this.rqBroker.doGet({
                url: url,
                opts: opts
            });
        }

        /**
         * Fill schedule gap/day
         *
         * e.g. es.api.schedule.doFill(177, 1025, 1672045200, [-6.47552, 53.34768]).done(console.log)
         * 
         * @param {number} scheduleId scheduleId
         * @param {number} layerViewId LayerViewId to query. If set to -1 then all point datasets in the map will be queried.
         * @param {number} date Date of day to fill as UNIX timestamp (seconds).
         * @param {number[]} location Optional. Location to use as a guide. Lon Lat coordinates.
         * @param {object} sortOrder optional. can be a layer attr sort order or a recommendation index sort order e.g. `{attr: 'POTENTIAL_VALUE', descending: true}` or `{attr: 'ES_RECOMMENDATION', descending: true}`
         * @param {number} distanceKms distance in kms to use as a buffer for the initial spatial search to identify candidates
         * @param opts
         * @returns {*}
         */
        doFill(scheduleId: number, layerViewId: number, date: number, location: [number, number]|undefined, sortOrder:SortOrder|undefined, distanceKms: number, opts?: ApiOpts) {
            if (!ValidateModule.checkId(scheduleId)) {
                return es.util.deferred.rejected();
            }
            
            let url = es.api.getURL("schedule_mobile/" + scheduleId + "/fill");

            const params = {
                "layer_view_id": layerViewId,
                "date": date,
                "location": location,
                "sort_order": sortOrder,
                "distance_kms": distanceKms,
            };

            url = es.util.url.fromParams(params, url + "?", true);

            return this.rqBroker.doPost({
                url: url,
                opts: opts,
                headers : {'es-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone}
            });
        }
    }




